<template>
  <b-card title="Edit PCC">
    <b-form @submit.prevent="updatePCC">
      <b-row>
        <b-col cols="12">
          <b-form-group label-for="title">
            <template slot="label">
              <span>Title <strong class="text-danger">*</strong></span>
            </template>
            <b-form-input id="title" required v-model="form.title" />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-row>
            <template v-for="(speaker, i) in speakers">
              <b-col
                cols="12"
                sm="12"
                :lg="speakers.length > 1 ? (i != 0 ? 5 : 7) : 7"
                :key="`speaker-${speaker.id}`"
              >
                <b-form-group label-for="speaker">
                  <template slot="label">
                    <span>Speaker <strong class="text-danger">*</strong></span>
                  </template>
                  <b-form-input
                    id="speaker"
                    required
                    v-model="speaker.speaker"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" sm="12" lg="5" :key="`position-${speaker.id}`">
                <b-form-group label-for="position">
                  <template slot="label">
                    <span>Position <strong class="text-danger">*</strong></span>
                  </template>
                  <b-form-input
                    id="position"
                    required
                    v-model="speaker.position"
                  />
                </b-form-group>
              </b-col>
              <b-col
                class="d-flex align-items-center mb-1 mb-lg-0"
                v-if="speakers.length > 1 && i > 0"
                cols="12"
                sm="12"
                lg="2"
                :key="`delete-${speaker.id}`"
              >
                <b-form-group class="mb-0 w-100">
                  <b-button
                    block
                    type="button"
                    variant="outline-secondary"
                    @click="removeSpeaker(speaker.id)"
                  >
                    Remove
                  </b-button>
                </b-form-group>
              </b-col>
            </template>
            <b-col cols="12" class="mb-1">
              <b-button
                @click="addSpeaker"
                variant="outline-primary"
                type="button"
              >
                Add Speaker
              </b-button>
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="12" sm="12" lg="6">
          <b-form-group label-for="category">
            <template slot="label">
              <span>Portfolio <strong class="text-danger">*</strong></span>
            </template>
            <v-select
              v-model="selectedCategories"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              multiple
              label="name"
              :options="productCategories"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" sm="12" lg="6">
          <b-form-group label-for="product">
            <template slot="label">
              <span>Product <strong class="text-danger">*</strong></span>
            </template>
            <v-select
              v-model="selectedProducts"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              multiple
              label="PRODUCT"
              :options="filteredProducts"
              :get-option-key="getProductOptionKey"
            />
          </b-form-group>
        </b-col>

        <b-col sm="12" lg="6">
          <b-form-group>
            <template slot="label">
              <span>Start Date <strong class="text-danger">*</strong></span>
            </template>
            <flat-pickr
              v-model="form.start_datetime"
              class="form-control"
              :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
            />
          </b-form-group>
        </b-col>

        <b-col sm="12" lg="6">
          <b-form-group>
            <template slot="label">
              <span>End Date <strong class="text-danger">*</strong></span>
            </template>
            <flat-pickr
              v-model="form.end_datetime"
              class="form-control"
              :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
            />
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label-for="address">
            <template slot="label">
              <span>Address <strong class="text-danger">*</strong></span>
            </template>
            <b-form-textarea
              rows="3"
              required
              id="address"
              v-model="form.address"
            />
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label-for="link">
            <template slot="label">
              <span>Link Conference</span>
            </template>
            <b-form-input
              id="link"
              v-model="form.link"
            />
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label-for="description">
            <template slot="label">
              <span>Description <strong class="text-danger">*</strong></span>
            </template>
            <snote v-model="form.description" ref="snote"/>
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label="Banner" label-for="dropzone-banner-image">
            <span class="text-muted font-italic">(1920 pixels x 1080 pixels)</span>
            <div id="dropzone-banner-image" class="mb-3 dropzone dropzone-area">
              <div class="dz-message">
                Drop files here or click button to upload. <br />(Max. size 10MB)
              </div>
            </div>
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label-for="dropzone-banner-home-image">
            <template slot="label">
              <span>Banner Home <strong class="text-danger">*</strong></span>
            </template>
            <span class="text-muted font-italic d-block mb-50">Same resolution as Banner Website: 1200px x 350px.</span>
            <div id="dropzone-banner-home-image" class="mb-3 dropzone dropzone-area">
              <div class="dz-message">
                Drop files here or click button to upload. <br />(Max. size 2MB)
              </div>
            </div>
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label-for="dropzone-banner-history-image">
            <template slot="label">
              <span>Banner History <span class="text-muted">(Optional)</span></span>
            </template>
            <span class="text-muted font-italic d-block mb-50">Same resolution as Banner Website: 1200px x 350px.</span>
            <span class="text-muted font-italic d-block mb-50">Replaces the Banner Home in the Home slider once this PCC is no longer active or its date has passed (shown only when the "Publish History" toggle is enabled in the PCC List)</span>
            <div id="dropzone-banner-history-image" class="mb-3 dropzone dropzone-area">
              <div class="dz-message">
                Drop files here or click button to upload. <br />(Max. size 2MB)
              </div>
            </div>
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label="Attachments" label-for="dropzone-attachments">
            <div id="dropzone-attachments" class="mb-3 dropzone dropzone-area">
              <div class="dz-message">
                Drop files here or click button to upload. <br />(Max. size 100MB)
              </div>
            </div>
            <b-card-text
              class="p-1 border rounded border-danger font-weight-bolder bold"
              v-for="attachment in form.attachments"
              :key="attachment.id"
            >
              <b-row>
                <b-col class="txt-xs-2" sm="6">
                  <feather-icon size="16" icon="FileTextIcon" />
                  <span class="ml-1">{{ attachment.file }}</span>
                </b-col>
                <b-col class="d-flex justify-content-end" sm="6">
                  <b-button
                    type="button"
                    class="mr-1 txt-xs-2"
                    size="sm"
                    variant="primary"
                    @click="downloadAttachment(attachment.id)"
                  >
                    <feather-icon size="14" icon="DownloadIcon" /> Download
                  </b-button>
                  <b-button
                    type="button"
                    @click="confirmDeleteAttachment(attachment.id)"
                    class="txt-xs-2"
                    size="sm"
                    variant="light"
                  >
                    <feather-icon size="14" icon="TrashIcon" /> Delete
                  </b-button>
                </b-col>
              </b-row>
            </b-card-text>
          </b-form-group>
        </b-col>

        <b-col sm="12">
          <b-form-group label="Video" label-for="dropzone-video">
            <div id="dropzone-video" class="dropzone dropzone-area mb-3">
              <div class="dz-message">
                Drop files here or click button to upload.
              </div>
            </div>
            <div v-if="video.length">
              <b-card-text
                v-for="vid in video" :key="vid.id"
                class="p-1 border rounded border-danger font-weight-bolder bold"
              >
                <b-row>
                  <b-col class="txt-xs-2" sm="6">
                    <feather-icon size="16" icon="FileTextIcon" />
                    <span class="ml-1">{{ vid.uuid == "0" ? vid.path.replace('pcc_video/','') : vid.path.replace('files/','') }}</span>
                  </b-col>
                  <b-col class="d-flex justify-content-end" sm="6">
                    <b-link target="_blank"
                        :href="vid.url">
                      <b-button
                        class="mr-1 txt-xs-2"
                        size="sm"
                        variant="primary"
                      >
                        <feather-icon size="14" icon="DownloadIcon" /> Download
                      </b-button>
                    </b-link>
                    <b-button
                      @click="confirmDeleteVideo(vid.path,vid.uuid)"
                      class="txt-xs-2"
                      size="sm"
                      variant="light"
                    >
                      <feather-icon size="14" icon="TrashIcon" /> Delete
                    </b-button>
                  </b-col>
                </b-row>
              </b-card-text>
            </div>
          </b-form-group>
        </b-col>

        <b-col sm="12" class="mt-2">
          <b-form-group class="d-flex justify-content-end">
            <b-button
              :to="{ name: 'admin-pcc-index' }"
              variant="light"
              class="mr-1"
            >
              Cancel
            </b-button>
            <b-button
              :disabled="isLoading"
              type="submit"
              variant="primary"
              class="mr-1"
            >
              Save
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BLink,
  BCard,
  BForm,
  BFormInput,
  BFormGroup,
  BRow,
  BCol,
  BFormSelect,
  BFormSelectOption,
  BButton,
  BFormTextarea,
  BTabs,
  BTab,
  BCardText,
} from 'bootstrap-vue'
import { quillEditor } from 'vue-quill-editor'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import snote from '../product/summernote.vue'

const PRODUCT_DROPDOWN_LIMIT = 100

export default {
  components: {
    BLink,
    BCard,
    BForm,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    BFormSelect,
    BFormSelectOption,
    quillEditor,
    BButton,
    BTabs,
    BTab,
    BCardText,
    BFormTextarea,
    flatPickr,
    vSelect,
    'snote': snote
  },

  data() {
    return {
      isLoading: false,
      editorOption: {
        theme: 'snow',
      },
      form: {
        id: '',
        title: '',
        start_datetime: '',
        end_datetime: '',
        address: '',
        description: '',
        banner_image: '',
        banner_image_home: '',
        banner_image_history: '',
        link: ''
      },
      pcc_video: [],
      temp_pcc_video: [],
      speakers: [{ id: Math.random(), speaker: '', position: '' }],
      selectedProducts: [],
      selectedCategories: [],
      attachments: [],
      video: [],
      productCategories: [],
      products: [],
      productRequestId: 0,
      isInitialLoad: false,
      imageThumbnail: null,
      dzBannerIns: null,
      dzBannerHomeIns: null,
      dzBannerHistoryIns: null,
      dzAttachmentsIns: null,
      // Old banner path to purge from storage after the form is saved successfully.
      // Kept separate from `form.banner_image_home/history` so removing a file only
      // affects local state until the user actually confirms via Save.
      pendingDeleteBannerHome: null,
      pendingDeleteBannerHistory: null,
    }
  },

  computed: {
    filteredProducts() {
      if (!this.selectedCategories.length) {
        return this.products
      }
      const selectedIds = new Set()
      const selectedNames = new Set()

      const normalizeId = value => {
        if (value === null || value === undefined || value === '') return null
        const stringValue = String(value).trim()
        if (stringValue === '') return null
        if (!Number.isNaN(Number(stringValue))) return String(Number(stringValue))
        return null
      }
      const normalizeName = value => {
        if (value === null || value === undefined || value === '') return null
        return String(value).trim().toLowerCase()
      }

      this.selectedCategories.forEach(category => {
        if (!category) return
        const ids = [
          category.id,
          category.ID,
          category.category_id,
          category.product_category_id,
        ]
        ids.forEach(id => {
          const normalized = normalizeId(id)
          if (normalized) selectedIds.add(normalized)
        })
        const names = [category.name, category.NAME, category.category_name]
        names.forEach(name => {
          const normalized = normalizeName(name)
          if (normalized) selectedNames.add(normalized)
        })
      })

      if (!selectedIds.size && !selectedNames.size) {
        return this.products
      }

      const extractProductCategory = product => {
        const ids = []
        const names = []
        const pushId = value => {
          const normalized = normalizeId(value)
          if (normalized) ids.push(normalized)
        }
        const pushName = value => {
          const normalized = normalizeName(value)
          if (normalized) names.push(normalized)
        }
        const pushRef = ref => {
          if (!ref || typeof ref !== 'object') return
          pushId(ref.id)
          pushId(ref.ID)
          pushId(ref.category_id)
          pushId(ref.product_category_id)
          pushName(ref.name)
          pushName(ref.NAME)
          pushName(ref.category_name)
        }

        pushRef(product.category)
        pushRef(product.product_category_details)
        pushRef(product.product_category)
        pushRef(product.category_details)

        pushId(product.category_id)
        pushId(product.categoryId)
        pushId(product.product_category_id)
        pushId(product.PROD_CATEGORY_ID)
        if (product.PROD_CATEGORY) {
          const normalizedId = normalizeId(product.PROD_CATEGORY)
          if (normalizedId) {
            ids.push(normalizedId)
          } else {
            pushName(product.PROD_CATEGORY)
          }
        }
        pushName(product.PROD_CATEGORY_NAME)

        return { ids, names }
      }

      return this.products.filter(product => {
        const { ids, names } = extractProductCategory(product)
        const hasId = ids.some(id => selectedIds.has(id))
        const hasName = names.some(name => selectedNames.has(name))
        return hasId || hasName
      })
    },
  },

  watch: {
    selectedCategories: {
      handler() {
        if (!this.selectedCategories.length) {
          this.products = []
          this.selectedProducts = []
          return
        }
        this.fetchProductsByCategories()
      },
      deep: true,
    },
    products() {
      this.syncSelectedProductsWithCategories()
    },
  },

  mounted() {
    this.initDropzone()
    Promise.all([
      this.fetchPCC(),
      this.getProductCategory(),
    ])
  },

  destroyed() {
    this.dzBannerIns.disable()
    this.dzBannerHomeIns.disable()
    this.dzBannerHistoryIns.disable()
    this.dzAttachmentsIns.disable()
  },

  methods: {
    initDropzone() {
      const optionsImg = {
        maxFiles: 1,
        maxFilesize:10,
        autoProcessQueue: true,
        autoQueue: true,
        addRemoveLinks: true,
        acceptedFiles: 'image/jpeg,image/jpg',
        headers: {
          Authorization: `Bearer ${this.$store.getters['oauth/getToken']}`,
        },
      }

      const optionsVideo = {
        maxFiles: 2,
        timeout: 1800000,
        autoProcessQueue: true,
        autoQueue: true,
        addRemoveLinks: true,
        acceptedFiles: "video/*",
        headers: {
          Authorization: `Bearer ${this.$store.getters['oauth/getToken']}`,
        },
      }

      const options = {
        maxFiles: 1,
        maxFilesize:100,
        autoProcessQueue: true,
        autoQueue: true,
        addRemoveLinks: true,
        headers: {
          Authorization: `Bearer ${this.$store.getters['oauth/getToken']}`,
        },
      }

      const { drawBannerImage, deleteTempFile } = this
      this.dzBannerIns = new window.Dropzone('#dropzone-banner-image', {
        url: `${process.env.VUE_APP_API_HOST}/api/pcc/banner/upload`,
        ...optionsImg,
        init() {
          this.on('success', (file, res) => drawBannerImage(res.data))
          this.on('removedfile', file => deleteTempFile(file))
        },
      })

      const { drawBannerImageHome, removeBannerImageHome } = this
      this.dzBannerHomeIns = new window.Dropzone('#dropzone-banner-home-image', {
        url: `${process.env.VUE_APP_API_HOST}/api/pcc/banner-home/upload`,
        ...optionsImg,
        maxFilesize: 2,
        init() {
          this.on('success', (file, res) => drawBannerImageHome(res.data))
          this.on('removedfile', () => removeBannerImageHome())
        },
      })

      const { drawBannerImageHistory, removeBannerImageHistory } = this
      this.dzBannerHistoryIns = new window.Dropzone('#dropzone-banner-history-image', {
        url: `${process.env.VUE_APP_API_HOST}/api/pcc/banner-history/upload`,
        ...optionsImg,
        maxFilesize: 2,
        init() {
          this.on('success', (file, res) => drawBannerImageHistory(res.data))
          this.on('removedfile', () => removeBannerImageHistory())
        },
      })

      const { drawAtachments, deleteTempAttachemts } = this
      this.dzAttachmentsIns = new window.Dropzone('#dropzone-attachments', {
        ...options,
        url: `${process.env.VUE_APP_API_HOST}/api/pcc/upload`,

        maxFiles: 5,
        maxFilesize: 50,
        init() {
          this.on('success', (file, res) => drawAtachments(file, res.data))
        },
      })

      const { drawVideo,deleteTempVideo } = this
      this.dzAttachmentsIns = new window.Dropzone('#dropzone-video', {
        ...optionsVideo,
        url: `${process.env.VUE_APP_API_HOST}/api/pcc/upload/video`,

        maxFilesize: 2000,
        init() {
          this.on('success', (file, res) => drawVideo(file, res.data))
          this.on('removedfile', file => deleteTempVideo(file))
        },
      })
    },

    drawBannerImage(imagePath) {
      this.form.banner_image = imagePath
    },

    drawBannerImageHome(imagePath) {
      this.form.banner_image_home = imagePath
    },

    drawBannerImageHistory(imagePath) {
      this.form.banner_image_history = imagePath
    },

    drawAtachments(file, path) {
      this.attachments.push({ uuid: file.upload.uuid, path })
    },

    drawVideo(file, path) {
      this.temp_pcc_video.push({ uuid: file.upload.uuid , path})
    },

    deleteTempAttachemts(file) {
      const deleteFileTempUrl = '/pcc/destroy'
      const uuid = file.upload.uuid
      const [attachment] = this.attachments.filter(
        attachment => attachment.uuid == uuid
      )

      this.$http
        .post(deleteFileTempUrl, {
          file: attachment.path,
        })
        .then(() => {
          this.attachments = this.attachments.filter(
            attachment => attachment.uuid != uuid
          )
        })
        .catch(({ data }) => {
          this.$notif('Error', `${data.message}`, 'error')
        })
    },

    deleteTempVideo(file) {
      const deleteFileTempUrl = '/pcc/destroy'
      const uuid = file.upload.uuid
      const [attachment] = this.pcc_video.filter(
        attachment => attachment.uuid == uuid
      )

      this.$http
        .post(deleteFileTempUrl, {
          file: attachment.path,
        })
        .then(() => {
          this.pcc_video = this.pcc_video.filter(
            attachment => attachment.uuid != uuid
          )
        })
        .catch(({ data }) => {
          this.$notif('Error', `${data.message}`, 'error')
        })
    },

    deleteTempFile() {
      const deleteFileTempUrl = `/pcc/banner/destroy/${this.$route.params.pccId}`

      if (this.form.banner_image) {
        this.$http
          .post(deleteFileTempUrl, {
            file: this.form.banner_image,
          })
          .then(() => {
            this.form.banner_image = ''
          })
          .catch(({ data }) => {
            this.$notif('Error', `${data.message}`, 'error')
          })
      }
    },

    // Clicking "Remove file" on an already-persisted banner must not delete it from
    // storage/DB right away — the banner is live on the Home slider. We only clear
    // the local form state here (so validation + UI reflect the removal immediately,
    // with no async race) and stash the old path. The actual destroy call is deferred
    // until updatePCC() succeeds, via cleanupPendingBannerDeletes().
    removeBannerImageHome() {
      if (this.form.banner_image_home) {
        this.pendingDeleteBannerHome = this.form.banner_image_home
      }
      this.form.banner_image_home = ''
    },

    removeBannerImageHistory() {
      if (this.form.banner_image_history) {
        this.pendingDeleteBannerHistory = this.form.banner_image_history
      }
      this.form.banner_image_history = ''
    },

    // Purges banners that were removed/replaced in this edit session, now that the
    // update has been confirmed saved. Best-effort: failures here shouldn't block the
    // user, they just leave an orphaned file for manual/background cleanup.
    cleanupPendingBannerDeletes() {
      if (this.pendingDeleteBannerHome) {
        this.$http
          .post(`/pcc/banner-home/destroy/${this.$route.params.pccId}`, {
            file: this.pendingDeleteBannerHome,
          })
          .catch(() => {})
        this.pendingDeleteBannerHome = null
      }

      if (this.pendingDeleteBannerHistory) {
        this.$http
          .post(`/pcc/banner-history/destroy/${this.$route.params.pccId}`, {
            file: this.pendingDeleteBannerHistory,
          })
          .catch(() => {})
        this.pendingDeleteBannerHistory = null
      }
    },

    async fetchPCC() {
      this.isInitialLoad = true
      return await this.$http
        .get(`/pcc/product/${this.$route.params.pccId}`)
        .then(({ data }) => {
          this.form = data.data
          this.selectedCategories = data.data.categories.map(
            cat => cat.category
          )
          this.selectedProducts = data.data.pcc_products.map(
            prod => prod.product
          )
          this.speakers = data.data.speakers
          this.video = data.data.video
          data.data.video.forEach(element => {
            this.temp_pcc_video.push({
              uuid: element.uuid,
              path: element.path,
            })
          });
          if(data.data.banner_image !== null){
            var mockFile = {
                type: 'image/png',
                accepted: true,
                url: data.data.banner_image_url
            };
            this.dzBannerIns.files.push(mockFile);
            this.dzBannerIns.emit("addedfile", mockFile);
            this.dzBannerIns.emit("thumbnail", mockFile, data.data.banner_image_url);
            this.dzBannerIns.emit("complete", mockFile);
          }
          if(data.data.banner_image_home !== null){
            var mockFileHome = {
                type: 'image/png',
                accepted: true,
                url: data.data.banner_image_home_url
            };
            this.dzBannerHomeIns.files.push(mockFileHome);
            this.dzBannerHomeIns.emit("addedfile", mockFileHome);
            this.dzBannerHomeIns.emit("thumbnail", mockFileHome, data.data.banner_image_home_url);
            this.dzBannerHomeIns.emit("complete", mockFileHome);
          }
          if(data.data.banner_image_history){
            var mockFileHistory = {
                type: 'image/png',
                accepted: true,
                url: data.data.banner_image_history_url
            };
            this.dzBannerHistoryIns.files.push(mockFileHistory);
            this.dzBannerHistoryIns.emit("addedfile", mockFileHistory);
            this.dzBannerHistoryIns.emit("thumbnail", mockFileHistory, data.data.banner_image_history_url);
            this.dzBannerHistoryIns.emit("complete", mockFileHistory);
          }
        })
        .then(() => {
          this.updateEditor()
        })
        .catch(({ data }) => {
          this.$notif('Error', `${data.message}`, 'error')
        })
    },

    async getProductCategory() {
      return await this.$http.get(`/product-category/take/all`).then(res => {
        this.productCategories = res.data.data.records
      })
    },

    async fetchProductsByCategories() {
      const categoryIds = this.selectedCategories
        .map(category =>
          category?.id ??
          category?.ID ??
          category?.category_id ??
          category?.product_category_id
        )
        .filter(id => id !== null && id !== undefined && id !== '')

      if (!categoryIds.length) {
        this.products = []
        this.selectedProducts = []
        return
      }

      const requestId = ++this.productRequestId
      this.products = []
      const mapRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          PROD_CATEGORY: 'd',
          product_category_details: 'd',
          category: 'd',
          category_id: 'd',
        },
      }

      try {
        const responses = await Promise.all(
          categoryIds.map(categoryId =>
            this.$http.post(
              `/product/browse/take/${PRODUCT_DROPDOWN_LIMIT}/skip/0/category/${categoryId}/orderBy.PRODUCT/asc/published/true`,
              mapRequest
            )
          )
        )
        if (requestId !== this.productRequestId) return
        const merged = []
        const seen = new Set()
        responses.forEach(({ data }) => {
          const records = data?.data?.records || []
          records.forEach(item => {
            const key = item?.KODE ?? item?.id ?? item?.PRODUCT
            if (!key || seen.has(key)) return
            seen.add(key)
            merged.push(item)
          })
        })
        this.products = merged
        this.$nextTick(() => {
          this.isInitialLoad = false
        })
      } catch (error) {
        this.isInitialLoad = false
        this.$notif('Error', `${error.message || error}`, 'error')
      }
    },

    getProductOptionKey(option) {
      return option?.KODE ?? option?.id ?? option?.PRODUCT
    },

    syncSelectedProductsWithCategories() {
      if (this.isInitialLoad) return
      if (!this.selectedCategories.length || !this.products.length) return
      const allowed = new Set(
        this.filteredProducts
          .map(product => product?.KODE)
          .filter(kode => kode !== null && kode !== undefined && kode !== '')
      )
      if (!allowed.size) return
      this.selectedProducts = this.selectedProducts.filter(product => {
        const kode = product?.KODE
        if (kode === null || kode === undefined || kode === '') return false
        return allowed.has(kode)
      })
    },

    updatePCC() {
      if (!this.form.banner_image_home) {
        this.$notif('Error', 'Banner Home is required.', 'error')
        return
      }

      this.isLoading = true
      this.$http
        .put(`/pcc/product/${this.$route.params.pccId}`, {
          ...this.form,
          pcc_video: this.temp_pcc_video,
          speakers: this.speakers.map(speaker => speaker.speaker),
          speaker_positions: this.speakers.map(speaker => speaker.position),
          categories_id: this.selectedCategories.map(cat => cat.id),
          products_kode: this.selectedProducts.map(product => product.KODE),
          attachments: this.attachments.map(item => item.path),
        })
        .then(() => {
          this.cleanupPendingBannerDeletes()
          this.$notif(
            'Success',
            'PCC has been successfully updated.',
            'success'
          )
          this.$router.push({ name: 'admin-pcc-index' })
        })
        .catch(({ data }) => {
          this.$notif('Error', `${data.message}`, 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    confirmDeleteAttachment(attachmentId) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deleteAttachment(attachmentId)
              .then(() => {
                this.$notif(
                  'Success',
                  'PCC Attachment has been successfully deleted.',
                  'success'
                )
                this.fetchPCC()
              })
              .catch(({ data }) => {
                this.$notif('Error', `${data.message}`, 'error')
              })
          }
        })
    },

    confirmDeleteVideo(path,uuid) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this video.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          const [attachment] = this.temp_pcc_video.filter(
            attachment => attachment.uuid == uuid
          )
          this.temp_pcc_video = this.temp_pcc_video.filter(
            attachment_new => attachment_new.uuid !== uuid
          )
          if (value === true) {
            this.$http
              .post('/pcc/destroy', {
                file: attachment.path,
                new: this.temp_pcc_video.length == 0 ? null : this.temp_pcc_video,
                id: this.form.id
              })
              .then(() => {
                this.$notif(
                  'Success',
                  'PCC Video has been successfully deleted.',
                  'success'
                )
                this.fetchPCC()
              })
              .catch(err => {
                console.log(err)
                // this.$notif('Error', `${data.message}`, 'error')
              })
          }
        })
    },

    async deleteAttachment(attachmentId) {
      return await this.$http.post(`pcc/delete/${attachmentId}`)
    },

    downloadAttachment(attachmentId) {
      return window.open(
        `${process.env.VUE_APP_API_HOST}/api/pcc/download/${attachmentId}`
      )
    },

    downloadVideo() {
      return window.open(
        `${process.env.VUE_APP_API_HOST}/api/pcc/video/${this.form.id}`
      )
    },

    addSpeaker() {
      this.speakers.push({
        id: Math.random(),
        speaker: '',
        position: '',
      })
    },

    removeSpeaker(id) {
      this.speakers = this.speakers.filter(speaker => speaker.id !== id)
    },
    updateEditor() {
        this.$refs.snote.updateEditor()
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
/deep/ .dz-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
